import { useEffect, useState } from 'react';

export interface CalendarEntry {
  vac: boolean;
  arr: boolean;
  arr_min: boolean;
  min_stay: number;
}

export interface CalendarEntryWithDate {
  [date: string]: CalendarEntry;
}

export interface BookingCalendar {
  calendar: CalendarEntryWithDate;
  minStay: number;
}

export interface RequestHook<T> {
  data?: T;
  error?: Error;
}

export async function fetchBookings(
  locale: string,
  propertyId: number,
  durationOfStay?: number | undefined | null,
): Promise<BookingCalendar> {
  let path = `/api/internal/${locale}/properties/${encodeURIComponent(propertyId)}/calendar`;

  if (durationOfStay != null && durationOfStay > 0) {
    path += `?duration_of_stay=${encodeURIComponent(durationOfStay)}`;
  }

  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  const response = await fetch(path, init);
  if (!response.ok) {
    throw new Error(`Could not retrieve calendar: ${response.statusText}`);
  }

  const json = (await response.json()) as { [date: string]: CalendarEntry };
  const minStay = Object.keys(json)
    .filter((key) => {
      const value = json[key];
      return value.arr_min;
    })
    .reduce<number | undefined>((prev, current) => {
      const value = json[current];
      if (!value) {
        return prev;
      }
      if (prev == null) {
        return value.min_stay;
      }
      return value.min_stay < prev && value.min_stay > 0 ? value.min_stay : prev;
    }, undefined);

  return {
    calendar: json,
    minStay: minStay != null ? minStay : 1,
  };
}

export function useFetchBookings(
  locale: string,
  propertyId: number,
  durationOfStay?: number | undefined | null,
): RequestHook<BookingCalendar> {
  const [data, setData] = useState<BookingCalendar | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (data != null) {
      setData(undefined);
    }
    if (error != null) {
      setError(undefined);
    }
    fetchBookings(locale, propertyId, durationOfStay)
      .then((d) => setData(d))
      .catch((e) => setError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, propertyId, durationOfStay]);

  return { data, error };
}
